/**
 * KEWELO homepage hero carousel.
 *
 * Dependency-free on purpose: two slides cross-fading needs a few dozen lines, not a carousel
 * library on the critical path of the largest paint on the site.
 *
 * Autoplay model — three bugs in the first version are the reason this is written the way it is:
 *
 *   1. Hover used to pause on the whole hero. The hero fills the first viewport, so a cursor
 *      simply resting on the page held the carousel frozen indefinitely and it looked broken.
 *      There is no hover pause now — scoping it to the controls only moved the freeze to
 *      "clicked an arrow and left the pointer on it".
 *   2. A manual arrow/dot press used to set a permanent `stopped` flag, so taking control once
 *      ended autoplay for the session. Manual input now suspends rotation for RESUME_DELAY and
 *      then hands it back.
 *   3. `disconnectedCallback` set that same permanent flag, so any re-render (section hydration,
 *      morph, the theme editor) killed autoplay for good — and `connectedCallback` never cleared
 *      it. Disconnect now only clears timers, and connect fully re-establishes state.
 *
 * The invariant that keeps this honest: nothing latches autoplay off forever except an explicit
 * reduced-motion preference. Every other pause has a matching resume.
 */

const RESUME_DELAY = 7000; // after manual input, before rotation resumes

class KeweloHero extends HTMLElement {
  #timer = null;
  #holdTimer = null;
  /** Reasons rotation is currently suspended. Autoplay runs only when this is empty. */
  #holds = new Set();
  #bound = false;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('.kw-hero__slide')];
    if (this.slides.length < 2) return;

    this.dots = [...this.querySelectorAll('.kw-hero__dot')];
    this.index = this.slides.findIndex((s) => s.dataset.active === 'true');
    if (this.index < 0) this.index = 0;

    this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayWanted = this.dataset.autoplay === 'true';
    this.interval = Math.max(4000, Number(this.dataset.interval) || 7000);

    // Re-entrant safe: a re-render can call connectedCallback again on the same instance, and
    // listeners must not stack up. State is rebuilt, holds are cleared, autoplay resumes.
    if (!this.#bound) {
      this.#bindEvents();
      this.#bound = true;
    }

    this.#holds.clear();
    if (document.hidden) this.#holds.add('hidden');
    this.#sync();
  }

  disconnectedCallback() {
    // Clear timers only. Never latch autoplay off here — this fires on ordinary re-renders.
    clearInterval(this.#timer);
    clearTimeout(this.#holdTimer);
    this.#timer = null;
    this.#holdTimer = null;
  }

  #bindEvents() {
    this.querySelectorAll('.kw-hero__arrow').forEach((btn) => {
      btn.addEventListener('click', () => this.#manual(this.index + Number(btn.dataset.dir)));
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.#manual(Number(dot.dataset.goto) - 1));
    });

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      this.#manual(this.index + (e.key === 'ArrowRight' ? 1 : -1));
    });

    // No hover pause at all, deliberately. Pausing on the hero froze it whenever a cursor
    // rested on the page (bug 1); pausing on just the controls froze it whenever someone
    // clicked an arrow and left the pointer there, which re-creates bug 2 by another route.
    // Rotation is held only by things that end on their own: keyboard focus, a hidden tab,
    // and the manual grace period.

    // Keyboard focus inside the hero means someone is working through it; don't move under
    // them. Gated on :focus-visible, because clicking an arrow with a mouse also focuses that
    // button — and an unconditional hold there left autoplay dead for every mouse user who
    // ever touched a control, which is the same failure as bug 2 wearing a different hat.
    this.addEventListener('focusin', () => {
      const el = document.activeElement;
      if (el && el.matches && el.matches(':focus-visible')) this.#hold('focus');
    });
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.#release('focus');
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#hold('hidden');
      else this.#release('hidden');
    });

    this.motionQuery.addEventListener('change', () => this.#sync());
  }

  /** A deliberate move: show it now, then give the visitor a beat before rotation resumes. */
  #manual(next) {
    this.go(next);
    this.#hold('manual');
    clearTimeout(this.#holdTimer);
    this.#holdTimer = setTimeout(() => this.#release('manual'), RESUME_DELAY);
  }

  #hold(reason) {
    this.#holds.add(reason);
    this.#sync();
  }

  #release(reason) {
    this.#holds.delete(reason);
    this.#sync();
  }

  /** Single place that decides whether the timer should be running. */
  #sync() {
    clearInterval(this.#timer);
    this.#timer = null;
    if (!this.autoplayWanted) return;
    if (this.motionQuery.matches) return; // reduced motion always wins
    if (this.#holds.size > 0) return;
    this.#timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  go(next) {
    const n = this.slides.length;
    const i = ((next % n) + n) % n;
    this.slides.forEach((slide, k) => {
      const active = k === i;
      // setAttribute, not toggleAttribute: toggling writes data-active="" while the stylesheet
      // matches [data-active='true'], so the incoming slide would stay transparent.
      if (active) slide.setAttribute('data-active', 'true');
      else slide.removeAttribute('data-active');
      // inert as well as aria-hidden: aria-hidden alone leaves the off-screen CTA tabbable.
      slide.toggleAttribute('inert', !active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.dots.forEach((dot, k) => dot.setAttribute('aria-selected', k === i ? 'true' : 'false'));
    this.index = i;
  }
}

if (!customElements.get('kewelo-hero')) customElements.define('kewelo-hero', KeweloHero);
